import { doc, getDoc, collection, query, where, getDocs, Timestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import dayjs from '../../utils/dayjsConfig';

/**
 * Fetches daily tips owed for a specific employee and date
 * @param {string} eid - Employee document ID
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Promise<Object|null>} - Tips data or null if not found
 */
export const getDailyTipsOwed = async (eid, dateString) => {
  try {
    const docId = `${eid}_${dateString}`;
    const docRef = doc(db, 'dailyTipsOwed', docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        tipsOwed: data.tipsOwed || 0,
        balanceAfter: data.balanceAfter || 0,
        date: dateString,
      };
    }

    return null;
  } catch (error) {
    console.error(`Error fetching daily tips for ${eid} on ${dateString}:`, error);
    return null;
  }
};

/**
 * Fetches daily tips owed for a week
 * @param {string} eid - Employee document ID
 * @param {Array<string>} dates - Array of date strings in YYYY-MM-DD format
 * @returns {Promise<Array>} - Array of tips data for each date
 */
export const getWeeklyTipsOwed = async (eid, dates) => {
  try {
    const promises = dates.map((dateString) => getDailyTipsOwed(eid, dateString));
    const results = await Promise.all(promises);
    return results;
  } catch (error) {
    console.error('Error fetching weekly tips:', error);
    throw error;
  }
};

/**
 * Fetches employee display name by employee ID
 * @param {string} eid - Employee document ID
 * @returns {Promise<string>} - Employee display name or fallback
 */
export const getEmployeeDisplayName = async (eid) => {
  try {
    if (!eid) return 'Unknown';

    const employeeRef = doc(db, 'employees', eid);
    const employeeSnap = await getDoc(employeeRef);

    if (employeeSnap.exists()) {
      const data = employeeSnap.data();
      return data.displayName || 'Unknown';
    }

    return 'Unknown';
  } catch (error) {
    console.error(`Error fetching employee ${eid}:`, error);
    return 'Unknown';
  }
};

/**
 * Fetches multiple employee names at once using batch queries
 * @param {Array<string>} eids - Array of employee document IDs
 * @returns {Promise<Object>} - Object mapping eid to displayName
 */
export const getEmployeeNamesByEid = async (eids) => {
  try {
    const uniqueEids = [...new Set(eids)].filter(Boolean);
    if (uniqueEids.length === 0) return {};

    const nameMap = {};

    // Firestore 'in' operator supports max 30 items per query, so batch if needed
    const BATCH_SIZE = 30;
    const batches = [];

    for (let i = 0; i < uniqueEids.length; i += BATCH_SIZE) {
      batches.push(uniqueEids.slice(i, i + BATCH_SIZE));
    }

    // Execute all batch queries in parallel
    const batchPromises = batches.map(async (batch) => {
      const q = query(collection(db, 'employees'), where('__name__', 'in', batch));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        nameMap[docSnap.id] = data.displayName || 'Unknown';
      });
    });

    await Promise.all(batchPromises);

    // Fill in 'Unknown' for any employee IDs that weren't found
    uniqueEids.forEach((eid) => {
      if (!(eid in nameMap)) {
        nameMap[eid] = 'Unknown';
      }
    });

    return nameMap;
  } catch (error) {
    console.error('Error fetching employee names:', error);
    return {};
  }
};

/**
 * Determines which day a payout belongs to based on venue timezone and endOfDay
 * @param {Timestamp} timestamp - Firestore timestamp
 * @param {string} venueTimezone - Venue timezone (e.g., 'America/Los_Angeles')
 * @param {number} endOfDay - Hour when day ends (0-23, e.g., 4 for 4 AM)
 * @returns {string} - Date string in YYYY-MM-DD format for the business day
 */
export const getBusinessDayForTimestamp = (timestamp, venueTimezone, endOfDay = 4) => {
  // Convert Firestore timestamp to dayjs in venue timezone
  const timestampMillis = timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000;
  const dateInVenueTz = dayjs(timestampMillis).tz(venueTimezone);

  // If the time is before endOfDay (e.g., before 4 AM), it belongs to the previous business day
  if (dateInVenueTz.hour() < endOfDay) {
    return dateInVenueTz.subtract(1, 'day').format('YYYY-MM-DD');
  }

  return dateInVenueTz.format('YYYY-MM-DD');
};

/**
 * Fetches tip payouts for an employee within a date range
 * @param {string} eid - Employee document ID
 * @param {Array<Object>} weekDates - Array of dayjs objects for the week
 * @param {string} venueTimezone - Venue timezone
 * @param {number} endOfDay - Hour when day ends (0-23)
 * @returns {Promise<Object>} - Object with payouts grouped by business day
 */
export const getWeeklyTipPayouts = async (eid, weekDates, venueTimezone, endOfDay = 4) => {
  try {
    // Get start and end timestamps for the query
    const firstDate = new Date(weekDates[0].toDate());
    const lastDate = new Date(weekDates[6].toDate());

    // Set to midnight to get the full day range
    firstDate.setHours(0, 0, 0, 0);
    lastDate.setHours(23, 59, 59, 999);

    // Convert to Firestore Timestamps
    const startTimestamp = Timestamp.fromDate(firstDate);
    const endTimestamp = Timestamp.fromDate(lastDate);

    // Query tipPayouts collection
    const q = query(
      collection(db, 'tipPayouts'),
      where('eid', '==', eid),
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp)
    );

    const tipPayoutsSnapshot = await getDocs(q);

    // Group payouts by business day
    const payoutsByDay = {};

    tipPayoutsSnapshot.docs.forEach((tipPayoutDoc) => {
      const tipData = tipPayoutDoc.data();
      const businessDay = getBusinessDayForTimestamp(tipData.timestamp, venueTimezone, endOfDay);

      if (!payoutsByDay[businessDay]) {
        payoutsByDay[businessDay] = [];
      }

      payoutsByDay[businessDay].push({
        id: tipPayoutDoc.id,
        amount: tipData.amount || 0,
        balanceAfter: tipData.balanceAfter || 0,
        paymentMethod: tipData.paymentMethod || '',
        paidByEid: tipData.paidByEid || '',
        paidByDisplayName: tipData.paidByDisplayName || null,
        timestamp: tipData.timestamp,
      });
    });

    return payoutsByDay;
  } catch (error) {
    console.error('Error fetching tip payouts:', error);
    throw error;
  }
};

/**
 * Fetches the most recent tip payout for an employee
 * @param {string} eid - Employee document ID
 * @returns {Promise<Object|null>} - Most recent payout data or null if no payouts found
 */
export const getMostRecentPayout = async (eid) => {
  try {
    // Query tipPayouts collection for this employee, ordered by timestamp descending, limit to 1
    const q = query(collection(db, 'tipPayouts'), where('eid', '==', eid), orderBy('timestamp', 'desc'), limit(1));

    const tipPayoutsSnapshot = await getDocs(q);

    if (tipPayoutsSnapshot.empty) {
      return null;
    }

    // Get the first (and only) document
    const doc = tipPayoutsSnapshot.docs[0];
    const data = doc.data();

    return {
      id: doc.id,
      timestamp: data.timestamp,
      amount: data.amount || 0,
      paymentMethod: data.paymentMethod || '',
      paidByEid: data.paidByEid || '',
      paidByDisplayName: data.paidByDisplayName || null,
      balanceAfter: data.balanceAfter || 0,
    };
  } catch (error) {
    console.error(`Error fetching most recent payout for ${eid}:`, error);
    return null;
  }
};
